import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProducts } from "../../hooks/useProducts";
import { Product, UpdateProductDto } from "../../types/product";
import { Pagination } from "../../components/Pagination";
import { EditProductModal } from "./EditProductModal";
import { ProductTableHeader } from "../../components/Products/ProductTableHeader";
import { ProductTableRow } from "../../components/Products/ProductTableRow";
import { DeactiveConfirmModal } from "../../components/Modal/DeactiveConfirmModal";
import { useProductStore } from "../../store/productStore";

const ITEMS_PER_PAGE = 20;

export const ProductList = () => {
  const { t } = useTranslation();
  const { products, isLoading, updateProduct } = useProducts();
  const searchQuery = useProductStore((state) => state.searchQuery);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showDeactiveConfirm, setShowDeactiveConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    
    const query = searchQuery.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.code.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredProducts]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const handleEdit = useCallback((product: Product) => {
    setSelectedProduct(product);
  }, []);

  const handleDelete = useCallback((product: Product) => {
    setProductToDelete(product);
    setShowDeactiveConfirm(true);
  }, []);

  const handleDeactivate = async () => {
    if (!productToDelete) return;


    try {
      const productData: UpdateProductDto = {
        name: productToDelete.name,
        description: productToDelete.description,
        price: productToDelete.price,
        availAreas: productToDelete.availAreas,
        logo: productToDelete.logo,
        status: 'DISABLED'
      };

      await updateProduct.mutateAsync({
        code: productToDelete.code,
        data: productData
      });
      setShowDeactiveConfirm(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Failed to deactivate product:", error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-xl overflow-x-auto">
        <table className="table w-full">
          <thead className="hidden md:table-header-group">
            <ProductTableHeader />
          </thead>
          <tbody>
            {paginatedProducts.map((product) => (
              <ProductTableRow
                key={product.code}
                product={product}
                onEdit={handleEdit}
                onDeactivate={handleDelete}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 py-4 px-4 md:px-6 bg-white rounded-xl">
        <div className="text-sm text-gray-500 order-2 md:order-1">
          {t("products.pagination.showing", {
            from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
            to: Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length),
            total: filteredProducts.length,
          })}
        </div>
        <div className="order-1 md:order-2 w-full md:w-auto flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <DeactiveConfirmModal
        isOpen={showDeactiveConfirm}
        onClose={() => setShowDeactiveConfirm(false)}
        onConfirm={handleDeactivate}
        isLoading={updateProduct.isPending}
      />

      {selectedProduct && (
        <EditProductModal
          isOpen={!!selectedProduct}
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};
